/**
 * Shared highlight context for path highlighting in both research and build graphs.
 *
 * Nodes and edges read this context directly to apply opacity/color,
 * avoiding the setNodes-overwrite problem where sync effects reset styles.
 */

import { createContext } from 'react';

export interface HighlightState {
  /** Node IDs on the highlighted path */
  nodeIds: Set<string>;
  /** Edge keys on the highlighted path (format: "sourceId-targetId" or connection_id) */
  edgeKeys: Set<string>;
  /** Whether highlighting is active */
  active: boolean;
  /**
   * What kind of selection drove this highlight, which decides how highlighted
   * edges treat the cluster header strips they cross:
   *  - `'node'`    — a node was selected; its arrows paint fully ABOVE the
   *                  headers (100%), because the node is the focus.
   *  - `'cluster'` — a cluster (its header) was selected; the header is the
   *                  focus, so the segment of a highlighted arrow that crosses
   *                  a header is ghosted to 30% over it (the rest stays 100%).
   * Undefined when highlighting is inactive.
   */
  mode?: 'node' | 'cluster';
}

export const EMPTY_HIGHLIGHT: HighlightState = {
  nodeIds: new Set(),
  edgeKeys: new Set(),
  active: false,
};

export const HighlightContext = createContext<HighlightState>(EMPTY_HIGHLIGHT);
